import {
  activeSchedule,
  schedulingSettingsReady,
  supportsScheduleProtocol
} from './scheduling_utils';
import { phaseLightingCatalog } from './light_phase_utils';
import {
  channelKey,
  channelLabel,
  hasLightChannelCapability,
  lightCatalogErrors,
  lightCycle,
  lightLayoutKnown,
  lightPhaseValuesKnown,
  lightStatusUnknown,
  timeMinutes
} from './light_profiles';

// Readback-only projection for Info. Drafts and editor preview state never enter it.
export function lightingInfoView(controller, parent) {
  if (!controller.access_rights?.lights?.read || !hasLightChannelCapability(controller))
    return null;
  const zone = Number(parent.zone_id ?? 0);
  const runtime = controller.settings.light_channels_info;
  const catalog = controller.settings.light_channels;
  const allocated = catalog?.schema_version === 1 ? catalog.channels : [];
  const channels = [...(allocated ?? []).filter((channel) => channel.zone === zone)];
  (runtime.channels ?? [])
    .filter((channel) => channel.zone === zone)
    .forEach((status) => {
      if (!channels.some((channel) => channelKey(channel) === channelKey(status)))
        channels.push(status);
    });
  const rows = channels
    .sort((a, b) => a.subzone - b.subzone || a.channel - b.channel)
    .map((channel) => ({
      ...channel,
      name: channelLabel(channel),
      status: runtime.channels?.find((status) => channelKey(status) === channelKey(channel))
    }));
  // Firmware without scheduling neither supplies recipes nor applies cached ones.
  const schedulingSupported = Boolean(runtime.capabilities & 4);
  const active = schedulingSupported ? activeSchedule(controller, parent) : null;
  const scheduling = parent.settings?.scheduling;
  const scheduleUnresolved = Boolean(
    // Lighting's phase mask does not report scheduling-only Time overrides.
    schedulingSupported &&
      (!controller.access_rights?.scheduling?.read ||
        !schedulingSettingsReady(scheduling) ||
        !supportsScheduleProtocol(controller, scheduling) ||
        (runtime.phase_zone_mask & (1 << zone) && !active) ||
        (scheduling?.schedule_meta?.schedule_enabled &&
          [1, 2].includes(scheduling.schedule_values?.runtime_status) &&
          !active))
  );
  const chartAvailable =
    !scheduleUnresolved &&
    parent.settings?.timing &&
    catalog?.schema_version === 1 &&
    catalog.revision === runtime.revision &&
    (!active?.metadata.schedule_lights_enabled || lightPhaseValuesKnown(active.phase.lighting)) &&
    lightLayoutKnown(runtime) &&
    !lightStatusUnknown(runtime) &&
    lightCatalogErrors(catalog, {}, catalog).length === 0;
  const sharedToplight =
    catalog?.zones?.find((entry) => entry.zone === zone)?.shared_toplight === 1;
  if (!chartAvailable) return { rows, runtime, zone, sharedToplight, chart: null };
  const projected = active?.metadata.schedule_lights_enabled
    ? phaseLightingCatalog(catalog, zone, active.phase.lighting)
    : catalog;
  const advanced = Boolean(
    (runtime.effective_advanced_zone_mask ?? projected.advanced_zone_mask) & (1 << zone)
  );
  const zoneSettings = projected.zones.find((entry) => entry.zone === zone);
  const subzones = projected.subzones.filter((entry) => entry.zone === zone);
  const sourceChannels = projected.channels
    .filter((channel) => channel.zone === zone)
    .map((channel) => {
      if (advanced) return channel;
      const main = zoneSettings.shared_toplight
        ? channel.subzone === 0 && channel.channel === 0
        : subzones.find((entry) => entry.subzone === channel.subzone)?.main_channel ===
          channel.channel;
      return { ...channel, points: [], _profile_off: !main };
    });
  return {
    rows,
    runtime,
    zone,
    sharedToplight,
    chart: {
      sourceChannels,
      subzones,
      zoneSettings,
      cycle: lightCycle(
        parent.settings.timing,
        active?.metadata.schedule_day_night_enabled ? active.phase : null
      ),
      sunriseDuration: parent.settings.lights?.light_sunrise_duration ?? 0,
      nowMinute: parent.infos?.time ? timeMinutes(parent.infos.time) : undefined
    }
  };
}

export const lightInfoValueId = (channel) =>
  `light_channel_${channel.zone}_${channel.subzone}_${channel.channel}`;

export function lightInfoStatus(status, t) {
  if (!status) return t('unknown');
  const flags = status.flags;
  return (
    [
      ...(flags & 1 ? [] : [t('lighting_info_automatic_off')]),
      ...(flags & 4 ? [t('lighting_manual_override')] : []),
      ...(flags & 8 ? [t('lighting_thermal_limitation')] : []),
      ...(flags & 16 ? [t('lighting_info_cutoff')] : []),
      ...(flags & 32 ? [t('lighting_info_fallback')] : []),
      ...(flags & ~63 ? [t('unknown')] : [])
    ].join(' · ') || t('lighting_info_automatic')
  );
}
